import { cardPack, cardPackName, cardsOpen, type CardText } from "@/lib/cards";
import type { Currency } from "@/lib/market";

// Client-side basket: lets a user stack multiple finished posters into one order.
// Each line is one finished poster (one jobId) plus the product chosen for it. Kept in
// localStorage so it survives navigating back to /upload to create another print.
// On checkout the lines become one Order per print (1 print = 1 order), paid together.
//
// A design can also sit in the basket a second time as Christmas cards: that line carries `card`
// (the inside text) and is told apart from the poster line by lineKey, not by jobId alone. A design
// has at most one card pack, always quantity 1: more cards is a bigger pack (setCardPack), and the
// card editor opens on the pack already in the cart and says that it changes it.

export type BasketLine = {
  jobId: string;
  /** Copies of this print. Always >= 1; absent on baskets saved before quantities existed. */
  quantity?: number;
  sku: string;
  productName: string;
  price: number;
  currency: Currency;
  frameColor?: string;
  // Canvas edge finish (Black / ImageWrap / MirrorWrap / White). Stored camelCase; mapped to
  // Prodigi's PascalCase server-side. Absent for non-canvas products.
  wrap?: string;
  orientation?: "portrait" | "landscape";
  title?: string;
  thumbnailUrl?: string;
  /** Present on a Christmas card pack: what prints inside the cards. */
  card?: CardText;
};

/** A line's identity: the design, plus whether it is the card pack of that design. */
export function lineKey(line: Pick<BasketLine, "jobId" | "card">): string {
  return line.card ? `${line.jobId.toLowerCase()}#card` : line.jobId.toLowerCase();
}

function sameLine(line: BasketLine, key: string): boolean {
  return lineKey(line) === key.toLowerCase();
}

const STORAGE_KEY = "vellum_basket";

// Fired on any basket mutation in this tab so the cart badge/drawer can react live
// (the native "storage" event only fires in *other* tabs).
export const BASKET_EVENT = "vellum-basket-changed";

// Fired to pull open the cart drawer from anywhere on the page. The drawer lives inside the
// header's CartButton, so a "go to cart" elsewhere (My designs) asks for it through the window
// rather than lifting the drawer's open state into a provider.
export const CART_OPEN_EVENT = "vellum-cart-open";

export function openCart() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CART_OPEN_EVENT));
}

function emitChange() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(BASKET_EVENT));
}

// Subscribe to basket changes (same-tab mutations + cross-tab storage events). Returns an unsubscribe.
export function subscribeBasket(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => callback();
  window.addEventListener(BASKET_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(BASKET_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

export function getBasketCount(): number {
  return getBasket().reduce((sum, l) => sum + Math.max(1, l.quantity ?? 1), 0);
}

/** Sets a line's copy count (by lineKey). Zero or less removes the line. */
export function setLineQuantity(key: string, quantity: number): BasketLine[] {
  const next = getBasket()
    .map((l) => (sameLine(l, key) ? { ...l, quantity: Math.floor(quantity) } : l))
    .filter((l) => (l.quantity ?? 1) > 0);
  save(next);
  return next;
}

// Christmas cards sell on the en (US) site only. On /nl, /de and /uk a card line is left out of the
// basket, so it never reaches a euro or pound checkout; CardsOffSiteNotice tells the shopper.
function cardsOffSite(): boolean {
  return /^\/(nl|de|uk)(\/|$)/.test(window.location.pathname);
}

/** True on /nl, /de or /uk while the stored basket holds a card line that getBasket leaves out. */
export function hasOffSiteCards(): boolean {
  return typeof window !== "undefined" && cardsOffSite() && readStored().some((l) => !!l.card);
}

export function getBasket(): BasketLine[] {
  if (typeof window === "undefined") return [];
  const lines = readStored();
  // After the Christmas deadline a card line can no longer be bought (the backend refuses it), so it
  // leaves the basket rather than blocking the poster next to it at checkout.
  return cardsOffSite() || !cardsOpen() ? lines.filter((l) => !l.card) : lines;
}

function readStored(): BasketLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (l): l is BasketLine =>
        !!l && typeof l === "object" && typeof (l as BasketLine).jobId === "string" && typeof (l as BasketLine).sku === "string",
    );
  } catch {
    return [];
  }
}

function save(lines: BasketLine[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    // ignore quota/serialization errors — basket is best-effort.
  }
  emitChange();
}

// Puts back the basket the server kept for a checkout intent, for a shopper who opened their
// recovery link somewhere the basket never existed. Refuses to overwrite a basket that is already
// there — a live basket is always the newer truth.
export function restoreBasket(lines: BasketLine[]): BasketLine[] {
  const current = getBasket();
  if (current.length || !lines.length) return current;
  save(lines);
  return lines;
}

// Adds a line, replacing any existing line for the same poster (one poster = one line, and one card
// pack per design). Job ids are matched case-insensitively: the same poster reaches us as both
// "69E4687E-…" and "69e4687e-…" depending on where the id came from, and a case difference used to
// stack two lines for one print.
export function addToBasket(line: BasketLine): BasketLine[] {
  const key = lineKey(line);
  const lines = getBasket().filter((l) => lineKey(l) !== key);
  lines.push({ ...line, ...(line.card ? { quantity: 1 } : {}) });
  save(lines);
  return lines;
}

/** The card pack of a design, if it is in the cart. */
export function cardLineFor(jobId: string): BasketLine | undefined {
  return getBasket().find((l) => !!l.card && l.jobId.toLowerCase() === jobId.toLowerCase());
}

/** Swaps a card line to another pack size (by lineKey). Its text stays; it is still one pack. */
export function setCardPack(key: string, sku: string): BasketLine[] {
  const pack = cardPack(sku);
  if (!pack) return getBasket();
  const next = getBasket().map((l) =>
    l.card && sameLine(l, key) ? { ...l, sku: pack.sku, productName: cardPackName(pack.sku), price: pack.price, quantity: 1 } : l,
  );
  save(next);
  return next;
}

// Patches every line of a design in place (e.g. to backfill a missing thumbnail, which the poster and
// the card pack of one design share). No-op if the design is gone.
export function updateBasketLine(jobId: string, patch: Partial<BasketLine>): BasketLine[] {
  const lines = getBasket();
  let changed = false;
  const next = lines.map((l) => {
    if (l.jobId !== jobId) return l;
    changed = true;
    return { ...l, ...patch };
  });
  if (changed) save(next);
  return next;
}

/** Removes one line, by lineKey. */
export function removeFromBasket(key: string): BasketLine[] {
  const lines = getBasket().filter((l) => !sameLine(l, key));
  save(lines);
  return lines;
}

/**
 * Takes the lines of a checkout out of the cart: each design's poster or card pack. Card lines this
 * site hides from the cart are not touched.
 */
export function removeBoughtLines(bought: { jobId: string; card: boolean }[]): BasketLine[] {
  if (typeof window === "undefined") return [];
  const stored = readStored();
  const isBought = (l: BasketLine) =>
    bought.some(
      (b) =>
        b.jobId.toLowerCase() === l.jobId.toLowerCase() &&
        b.card === !!l.card,
    );
  const next = stored.filter((l) => !isBought(l));
  if (next.length !== stored.length) save(next);
  return getBasket();
}
